import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import { CameraIcon } from "@/components/icons/camera-icon";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { EXTRA_FOOD_DEFAULT_MEAL_TIME } from "@/features/meal-plan/constants/extra-food";
import { useToast } from "@/hooks/ui/use-toast";
import { useTakeRecipePhoto } from "@/hooks/utils/use-take-recipe-photo";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useEffect, useRef } from "react";
import { SaveMealAsModal } from "../save-meal-as-modal";
import { AddFoodModalMode, buildRecipeProcessingRoute } from "./recipe-processing-route";

interface PhotoOptionProps {
  disabled?: boolean;
  onComplete: () => void;
  mode?: AddFoodModalMode;
  day?: string;
  targetDate?: string;
  mealTime?: Body_add_your_food_api_v1_meals_your_food_post.meal_time;
}

export const PhotoOption = memo(function PhotoOption({
  disabled,
  onComplete,
  mode = "default",
  day,
  targetDate,
  mealTime,
}: PhotoOptionProps) {
  const {
    takePhoto,
    uploadPhoto,
    abandonPhoto,
    capturedPhoto,
    isLoading: isUploading,
  } = useTakeRecipePhoto();
  const { showError } = useToast();
  const isAutoUploadingRef = useRef(false);

  // Handle taking a photo
  const handleClick = useCallback(async () => {
    await takePhoto();
  }, [takePhoto]);

  const handleSelectMealTime = useCallback(
    async (selectedMealTime: Body_add_your_food_api_v1_meals_your_food_post.meal_time) => {
      try {
        const uploadedMealId = await uploadPhoto(selectedMealTime, (mealId) =>
          buildRecipeProcessingRoute({
            mealId,
            mode,
            day,
            targetDate,
            mealTime: selectedMealTime,
          }),
        );
        if (!uploadedMealId) {
          return;
        }
        onComplete();
      } catch (error) {
        showError("Failed to add your food. Please try again.");
        console.error("Error adding your food from photo:", error);
      }
    },
    [uploadPhoto, onComplete, showError, mode, day, targetDate],
  );

  // Handle cancel meal time selection
  const handleCancelMealTime = useCallback(() => {
    abandonPhoto();
  }, [abandonPhoto]);

  useEffect(() => {
    if (!capturedPhoto || isAutoUploadingRef.current) {
      return;
    }

    const autoMealTime =
      mode === "extra-food"
        ? EXTRA_FOOD_DEFAULT_MEAL_TIME
        : mode === "add-to-meal-time"
          ? mealTime
          : undefined;

    if (!autoMealTime) {
      return;
    }

    isAutoUploadingRef.current = true;
    void (async () => {
      try {
        const uploadedMealId = await uploadPhoto(autoMealTime, (mealId) =>
          buildRecipeProcessingRoute({
            mealId,
            mode,
            day,
            targetDate,
            mealTime: autoMealTime,
          }),
        );
        if (!uploadedMealId) {
          abandonPhoto();
          return;
        }
        onComplete();
      } catch (error) {
        showError("Failed to add your food. Please try again.");
        console.error("Error adding your food from photo:", error);
        abandonPhoto();
      } finally {
        isAutoUploadingRef.current = false;
      }
    })();
  }, [
    mode,
    mealTime,
    capturedPhoto,
    uploadPhoto,
    day,
    targetDate,
    onComplete,
    showError,
    abandonPhoto,
  ]);

  return (
    <>
      <OutlinedButton
        color="black"
        textCase="normal"
        disabled={disabled || isUploading}
        onClick={() => {
          void handleClick();
        }}
        className="flex items-center justify-between px-3"
      >
        <CameraIcon className="h-6 w-6" />
        <span className={textVariants.bodySingleLine}>Take a photo of your food</span>
        <div className="h-6 w-6" />
      </OutlinedButton>

      {mode === "default" && (
        <SaveMealAsModal
          isOpen={!!capturedPhoto}
          onDismiss={handleCancelMealTime}
          disabled={isUploading}
          onSelectMealTime={handleSelectMealTime}
        />
      )}
    </>
  );
});
